import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import Joi from 'joi'
import { t } from 'i18next'
import NumberFormat from 'react-number-format'
import { ShieldOutlined } from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { delete2FAUser, getQr, setQr, toggle2step } from '../../store/settings/actionCreator'
import { ErrorsState, validateForm } from '../../function/validation'
import { Err, Shell } from './ChangeDialogsV2'
import s from './ChangeDialogsV2.module.css'

/*
 * 2-step verification in the v2 admin design — the classic flows
 * (container/settings/Popup2FAQr + Popup2FAInput) in one dialog:
 *   enable     — scan the QR code (GET it as the classic one does), then the
 *                app's code turns it on;
 *   disable    — the code turns it off;
 *   deleteUser — the code deletes the account (while 2FA is on).
 * The same store actions; the classic settings page keeps the classic dialogs.
 */

const schema = Joi.object({
  code: Joi.string().required().min(6).label(t('field.label.verificationCode')),
})

type Mode = 'enable' | 'disable' | 'deleteUser'

export function TwoFactorV2({ mode, onClose }: { mode: Mode; onClose: () => void }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { is2FAEnabled, twoFactorQr } = useSelector((state) => state.settings)
  const [step, setStep] = React.useState<'scan' | 'code'>(mode === 'enable' ? 'scan' : 'code')
  const [code, setCode] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  // the QR code, only for turning it on — dropped again when the dialog goes
  React.useEffect(() => {
    if (mode !== 'enable') return undefined
    dispatch(getQr)
    return () => { dispatch(setQr(null)) }
  }, [dispatch, mode])

  React.useEffect(() => { setErrors(null) }, [code])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (step === 'scan') { setStep('code'); return }
    const error = validateForm(schema, { code })
    if (error) { setErrors(error); return }
    if (mode === 'deleteUser') dispatch(delete2FAUser(code, navigate))
    else dispatch(toggle2step(code, !is2FAEnabled, navigate))
    onClose()
  }

  const icon = <ShieldOutlined sx={{ fontSize: 19 }} />
  const title = t('auth.2step.title')

  if (step === 'scan') {
    return (
      <Shell icon={icon} title={title} sub="Step 1 of 2 — connect an authenticator app" onClose={onClose} onSubmit={submit} saveLabel="Next">
        <ol className={s.steps}>
          <li>Open an authenticator app on your phone — Google Authenticator, Microsoft Authenticator, 1Password…</li>
          <li>Add an account and scan this code.</li>
        </ol>
        <div className={s.qr}>
          {twoFactorQr ? <img src={twoFactorQr} alt="QR code for your authenticator app" /> : <span className={s.qrWait}>Loading the code…</span>}
        </div>
        <span className={s.note}>{t('settings.2factorHint2')}</span>
      </Shell>
    )
  }

  const texts: Record<Mode, { sub: string; save: string; danger: boolean }> = {
    enable: { sub: 'Step 2 of 2 — enter the code from the app to turn it on', save: 'Turn on', danger: false },
    disable: { sub: 'Enter the code from your authenticator app to turn it off', save: 'Turn off', danger: true },
    deleteUser: { sub: 'Enter the code from your authenticator app to delete your account', save: 'Delete account', danger: true },
  }
  const x = texts[mode]
  return (
    <Shell
      icon={icon}
      title={title}
      sub={x.sub}
      onClose={onClose}
      onSubmit={submit}
      saveLabel={x.save}
      danger={x.danger}
      cancelLabel={mode === 'enable' ? 'Back' : undefined}
      onCancel={mode === 'enable' ? () => setStep('scan') : undefined}
    >
      <label className={s.field}>
        <span className={s.label}>{t('field.label.verificationCode')}</span>
        <NumberFormat
          className={`${s.input} ${s.bigCode} ${errors?.code ? s.invalid : ''}`}
          value={code}
          onValueChange={(values) => setCode(values.value)}
          format="### ###"
          mask="_"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
        />
        <Err error={errors?.code} />
      </label>
      <span className={s.note}>The 6-digit code changes every 30 seconds.</span>
    </Shell>
  )
}
